import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { ICameraVideoTrack } from "agora-rtc-react";
import { VideocamOffOutlined } from "@mui/icons-material";
import { MeetStyleVideoPlayer } from "../MeetStyleVideoPlayer";

const PIP_EDGE_MARGIN = 28;
const PIP_TOP_MARGIN = 16;
const PIP_BOTTOM_RESERVED = 100;

interface DraggableLocalVideoProps {
  localCameraTrack: ICameraVideoTrack | null;
  localVideoMuted: boolean;
  participantName: string;
  windowWidth: number;
  windowHeight: number;
}

const getDefaultPosition = (
  containerWidth: number,
  containerHeight: number,
  videoWidth: number,
  videoHeight: number,
) => ({
  x: Math.max(PIP_EDGE_MARGIN, containerWidth - videoWidth - PIP_EDGE_MARGIN),
  y: PIP_TOP_MARGIN,
});

export const DraggableLocalVideo: React.FC<DraggableLocalVideoProps> = ({
  localCameraTrack,
  localVideoMuted,
  participantName,
  windowWidth,
  windowHeight,
}) => {
  const videoWidth = Math.min(160, windowWidth * 0.25);
  const videoHeight = videoWidth * (4 / 3);

  const [bounds, setBounds] = useState({
    width: windowWidth,
    height: windowHeight,
  });
  const [position, setPosition] = useState(() =>
    getDefaultPosition(windowWidth, windowHeight, videoWidth, videoHeight),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const parent = containerRef.current?.parentElement;
    if (!parent) {
      return;
    }

    const updateBounds = () => {
      const nextBounds = {
        width: parent.clientWidth,
        height: parent.clientHeight,
      };
      setBounds(nextBounds);
      setPosition((current) => {
        const maxX = nextBounds.width - videoWidth - PIP_EDGE_MARGIN;
        const maxY = nextBounds.height - videoHeight - PIP_BOTTOM_RESERVED;
        return {
          x: Math.min(Math.max(PIP_EDGE_MARGIN, current.x), Math.max(PIP_EDGE_MARGIN, maxX)),
          y: Math.min(Math.max(PIP_TOP_MARGIN, current.y), Math.max(PIP_TOP_MARGIN, maxY)),
        };
      });
    };

    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    observer.observe(parent);

    return () => observer.disconnect();
  }, [videoWidth, videoHeight]);

  useEffect(() => {
    setPosition(
      getDefaultPosition(bounds.width, bounds.height, videoWidth, videoHeight),
    );
  }, [bounds.width, bounds.height, videoWidth, videoHeight]);

  const clampPosition = useCallback((x: number, y: number) => {
    const maxX = bounds.width - videoWidth - PIP_EDGE_MARGIN;
    const maxY = bounds.height - videoHeight - PIP_BOTTOM_RESERVED;

    return {
      x: Math.max(PIP_EDGE_MARGIN, Math.min(x, Math.max(PIP_EDGE_MARGIN, maxX))),
      y: Math.max(PIP_TOP_MARGIN, Math.min(y, Math.max(PIP_TOP_MARGIN, maxY))),
    };
  }, [bounds.height, bounds.width, videoHeight, videoWidth]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      setPosition(
        clampPosition(e.clientX - dragStart.x, e.clientY - dragStart.y),
      );
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      const touch = e.touches[0];
      setPosition(
        clampPosition(touch.clientX - dragStart.x, touch.clientY - dragStart.y),
      );
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, dragStart, bounds.width, bounds.height, videoWidth, videoHeight, clampPosition]);

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        width: videoWidth,
        height: videoHeight,
        boxSizing: "border-box",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        cursor: isDragging ? "grabbing" : "grab",
        zIndex: 100,
        border: "2px solid #fff",
        backgroundColor: "#000",
        touchAction: "none",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {!localVideoMuted ? (
        <MeetStyleVideoPlayer
          track={localCameraTrack}
          play={!localVideoMuted}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#1a1a1a",
          }}
        >
          <VideocamOffOutlined sx={{ fontSize: 40, color: "#666" }} />
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: 8,
          right: 8,
          bottom: 8,
          padding: "5px 8px",
          borderRadius: 8,
          backgroundColor: "rgba(0,0,0,0.65)",
          color: "#fff",
          fontSize: 12,
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
        }}
        title={participantName}
      >
        {participantName}
      </div>
    </div>
  );
};

export default DraggableLocalVideo;
